import * as Sentry from '@sentry/remix';

export type NotificationProduct = {
  product_id: string;
  sizes: string[];
};

export const PENDING_REGISTRATION_EMAIL_KEY = 'pendingRegistrationEmail';

export const PENDING_REGISTRATION_SET_AT_KEY = 'pendingRegistrationEmailSetAt';

const PENDING_REGISTRATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const PENDING_REGISTRATION_RENEW_AFTER_MS = 60 * 60 * 1000;

export const PENDING_REGISTRATION_EMAIL_CHANGED_EVENT =
  'pending-registration-email-changed';

function emitPendingRegistrationEmailChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PENDING_REGISTRATION_EMAIL_CHANGED_EVENT));
  }
}

function renewPendingRegistrationExpiry() {
  const setAt = Number(localStorage.getItem(PENDING_REGISTRATION_SET_AT_KEY));

  if (setAt && Date.now() - setAt < PENDING_REGISTRATION_RENEW_AFTER_MS) return;

  localStorage.setItem(PENDING_REGISTRATION_SET_AT_KEY, String(Date.now()));
}

function removeStoredPendingRegistration() {
  localStorage.removeItem(PENDING_REGISTRATION_EMAIL_KEY);
  localStorage.removeItem(PENDING_REGISTRATION_SET_AT_KEY);
}

export function getPendingRegistrationEmail(): string | null {
  try {
    const email = localStorage.getItem(PENDING_REGISTRATION_EMAIL_KEY);

    if (!email) return null;

    const setAt = Number(localStorage.getItem(PENDING_REGISTRATION_SET_AT_KEY));

    if (!setAt) {
      localStorage.setItem(PENDING_REGISTRATION_SET_AT_KEY, String(Date.now()));

      return email;
    }

    if (Date.now() - setAt > PENDING_REGISTRATION_TTL_MS) {
      removeStoredPendingRegistration();

      return null;
    }

    return email;
  } catch {
    return null;
  }
}

export function setPendingRegistrationEmail(email?: string) {
  if (!email) return false;

  try {
    const normalisedEmail = email.toLowerCase();
    const storedEmail = localStorage.getItem(PENDING_REGISTRATION_EMAIL_KEY);

    if (storedEmail === normalisedEmail) {
      renewPendingRegistrationExpiry();

      return false;
    }

    localStorage.setItem(PENDING_REGISTRATION_EMAIL_KEY, normalisedEmail);
    localStorage.setItem(PENDING_REGISTRATION_SET_AT_KEY, String(Date.now()));
    emitPendingRegistrationEmailChanged();

    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'pending-registration-confirmation' }
    });

    return false;
  }
}

export function clearPendingRegistrationEmail() {
  try {
    if (localStorage.getItem(PENDING_REGISTRATION_EMAIL_KEY) === null) return;

    removeStoredPendingRegistration();
    emitPendingRegistrationEmailChanged();
  } catch {
    // localStorage unavailable — nothing to clear
  }
}

export function getBrowser(userAgent: string | null) {
  const match = userAgent?.match(/firefox|chrome|safari|instagram|msie/i);

  if (!match) return 'unknown';

  const [browser] = match;

  return browser!.toLowerCase();
}

export function isAppleMobileDevice(userAgent: string | null) {
  if (!userAgent) return false;

  return /iPhone|iPad|iPod/i.test(userAgent);
}

export function isInAppBrowser(userAgent: string = '') {
  return /instagram|fban|fbav|twitter/i.test(userAgent);
}

export function buildNotificationProducts(
  productId?: string | number,
  sizes: string[] = ['ALL']
): NotificationProduct[] {
  const id = Number(productId);

  if (!id) return [];

  return [{ product_id: String(id), sizes: sizes.length ? sizes : ['ALL'] }];
}
